import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Menu,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as DeliveredIcon,
  Info as InfoIcon,
  MarkEmailRead as MarkReadIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { resolveNotificationMessage } from '../utils/notificationMessages';
import { getNotificationRoute } from '../utils/notificationRoutes';
import moment from 'moment';

const NotificationBell = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { notifications, unreadCount, isConnected, markAsRead, markAllAsRead, clearNotifications } = useNotifications();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    const route = getNotificationRoute(notification);
    if (route) {
      handleClose();
      navigate(route);
    }
  };

  const getNotificationIcon = (notification) => {
    if (notification.type === 'package_update') {
      if (notification.station?.finalStation || notification.status?.toLowerCase() === 'delivered') {
        return <DeliveredIcon color="success" />;
      } else {
        return <ShippingIcon color="primary" />;
      }
    }
    return <InfoIcon color="info" />;
  };

  const formatTime = (timestamp) => {
    return moment(timestamp).fromNow();
  };

  const open = Boolean(anchorEl);

  return (
    <Box>
      {/* Notification Bell Button */}
      <IconButton
        onClick={handleClick}
        color="inherit"
        sx={{
          position: 'relative',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              minWidth: '16px',
              height: '16px'
            }
          }}
        >
          <NotificationsIcon />
        </Badge>
        
        {/* Connection Status Indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: isConnected ? 'green' : 'red',
            border: '1px solid white'
          }}
        />
      </IconButton>

      {/* Notifications Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            '& .MuiMenuItem-root': {
              padding: 0
            }
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </Typography>
            <Box>
              {unreadCount > 0 && (
                <IconButton size="small" onClick={markAllAsRead} title="Mark all as read">
                  <MarkReadIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={clearNotifications} title="Clear all">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          {/* Connection Status */}
          <Chip
            size="small"
            label={isConnected ? "Connected" : "Disconnected"}
            color={isConnected ? "success" : "error"}
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: notification.isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                      backgroundColor: notification.isRead 
                        ? 'rgba(0, 0, 0, 0.04)' 
                        : 'rgba(25, 118, 210, 0.12)'
                    },
                    px: 2,
                    py: 1.5
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getNotificationIcon(notification)}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: notification.isRead ? 'normal' : 'bold',
                            mb: 0.5,
                            flex: 1,
                            pr: 1
                          }}
                        >
                          {resolveNotificationMessage(notification)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {formatTime(notification.timestamp)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      notification.tracking && (
                        <Typography variant="caption" color="text.secondary">
                          Package: {notification.tracking}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
            <Button variant="outlined" color="secondary" size="small" onClick={handleClose}>
              Close
            </Button>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default NotificationBell;